// src/pages/IndustryApplications/IndustryData.jsx
import React from 'react';
import { useTranslation } from 'react-i18next';

export const useIndustryData = () => {
    const { t } = useTranslation();

    // Helper to safely render bullet points from the JSON arrays
    const renderInfoReason = (translationPath) => {
        const items = t(translationPath, { returnObjects: true });
        if (!Array.isArray(items)) return null;
        
        return (
            <ul style={{ paddingLeft: '14px', margin: 0, listStyleType: 'disc', textAlign: 'left' }}>
                {items.map((item, idx) => (
                    <li 
                        key={idx} 
                        style={{ marginBottom: idx === items.length - 1 ? '0' : '4px' }} 
                        dangerouslySetInnerHTML={{ __html: item }} 
                    />
                ))}
            </ul>
        );
    };

    // Pre-fetch array regions for text highlights
    const newsRegions = t('industryApplications.data.news.solutions.semanticDetection.regions', { returnObjects: true }) || [];
    const academiaRegions = t('industryApplications.data.academia.solutions.textVerification.regions', { returnObjects: true }) || [];

    return [
        {
            id: 'advertising',
            title: t('industryApplications.data.advertising.title'),
            subtitle: t('industryApplications.data.advertising.subtitle'),
            problem: t('industryApplications.data.advertising.problem'),
            impact: t('industryApplications.data.advertising.impact'),
            solutionPoints: [
                { 
                    title: t('industryApplications.data.advertising.solutions.deepfakePrevention.title'), 
                    text: t('industryApplications.data.advertising.solutions.deepfakePrevention.text'),
                    renderType: 'heatmap',
                    regions: [
                        { id: 'text', box: [0.46, 0.17, 0.95, 0.46], reason: t('industryApplications.data.advertising.solutions.deepfakePrevention.regions.text'), severity: 10, position: 'left' },
                        { id: 'live_badge', box: [0.83, 0.0, 1.0, 0.11], reason: t('industryApplications.data.advertising.solutions.deepfakePrevention.regions.live_badge'), severity: 8, position: 'bottom' },
                        { id: 'face', box: [0.13, 0.04, 0.37, 0.58], reason: t('industryApplications.data.advertising.solutions.deepfakePrevention.regions.face'), severity: 9, position: 'right' }
                    ],
                    infoPoint: {
                        id: 'info', box: [0.935, 0.88, 0.00, 0.00], position: 'top',
                        title: t('industryApplications.data.advertising.solutions.deepfakePrevention.infoTitle'),
                        reason: renderInfoReason('industryApplications.data.advertising.solutions.deepfakePrevention.infoReason')
                    }
                },
                { 
                    title: t('industryApplications.data.advertising.solutions.vast.title'), 
                    text: t('industryApplications.data.advertising.solutions.vast.text'),
                    renderType: 'shield',
                    mediaType: 'video',
                    mediaUrl: 'https://vetra-ai-public-content.s3.eu-central-1.amazonaws.com/video_unbranded.mp4',
                    verifyUrl: 'https://www.vetra-ai.com/verify?id=6bb4df7b2e06e96a799e3b527acaa3c5ef5bc39b563fb3fd272f75156642005a&ref=c8003531-004f-43aa-8b9b-5b7e4ae5c5e9&ctx=a76ccc49e51ae4a06a6cb6ae0f762fe60f216fd56a4d266810ee8475ad282491&context=ai_detection&context=fake_news_detection&regions=true&explanation=true',
                    shieldIcon: require('./assets/application/vetraai_logo_advertising_1.webp'),
                    shieldPosition: 'top-right'
                }
            ]
        },
        {
            id: 'insurance',
            title: t('industryApplications.data.insurance.title'),
            subtitle: t('industryApplications.data.insurance.subtitle'),
            problem: t('industryApplications.data.insurance.problem'),
            impact: t('industryApplications.data.insurance.impact'),
            solutionPoints: [
                { 
                    title: t('industryApplications.data.insurance.solutions.heatmaps.title'), 
                    text: t('industryApplications.data.insurance.solutions.heatmaps.text'),
                    renderType: 'heatmap',
                    regions: [
                        { id: 'debris', box: [0.25, 0.78, 0.55, 0.98], reason: t('industryApplications.data.insurance.solutions.heatmaps.regions.debris'), severity: 8, position: 'top' },
                        { id: 'wheel_well', box: [0.42, 0.58, 0.56, 0.82], reason: t('industryApplications.data.insurance.solutions.heatmaps.regions.wheel_well'), severity: 9, position: 'left' },
                        { id: 'red_car', box: [0.8, 0.32, 0.9, 0.39], reason: t('industryApplications.data.insurance.solutions.heatmaps.regions.red_car'), severity: 7, position: 'left' },
                        { id: 'street_sign', box: [0.89, 0.21, 0.94, 0.26], reason: t('industryApplications.data.insurance.solutions.heatmaps.regions.street_sign'), severity: 8, position: 'bottom' },
                        { id: 'white_pillar', box: [0.19, 0.0, 0.25, 0.4], reason: t('industryApplications.data.insurance.solutions.heatmaps.regions.white_pillar'), severity: 6, position: 'right' }
                    ],
                    infoPoint: {
                        id: 'info', box: [0.935, 0.9, 0.00, 0.00], position: 'top',
                        title: t('industryApplications.data.insurance.solutions.heatmaps.infoTitle'),
                        reason: renderInfoReason('industryApplications.data.insurance.solutions.heatmaps.infoReason')
                    }
                },
                { 
                    title: t('industryApplications.data.insurance.solutions.apiTriage.title'), 
                    text: t('industryApplications.data.insurance.solutions.apiTriage.text'),
                    renderType: 'pipeline'
                }
            ]
        },
        {
            id: 'cybersecurity',
            title: t('industryApplications.data.cybersecurity.title'),
            subtitle: t('industryApplications.data.cybersecurity.subtitle'),
            problem: t('industryApplications.data.cybersecurity.problem'),
            impact: t('industryApplications.data.cybersecurity.impact'),
            solutionPoints: [
                { 
                    title: t('industryApplications.data.cybersecurity.solutions.identityDetection.title'), 
                    text: t('industryApplications.data.cybersecurity.solutions.identityDetection.text'),
                    renderType: 'heatmap',
                    regions: [
                        { id: 'hair', box: [0.15, 0.0, 0.75, 0.20], reason: t('industryApplications.data.cybersecurity.solutions.identityDetection.regions.hair'), severity: 8, position: 'left' },
                        { id: 'skin', box: [0.58, 0.55, 0.75, 0.85], reason: t('industryApplications.data.cybersecurity.solutions.identityDetection.regions.skin'), severity: 9, position: 'right' },
                        { id: 'clothing', box: [0.1, 0.75, 0.85, 1], reason: t('industryApplications.data.cybersecurity.solutions.identityDetection.regions.clothing'), severity: 7, position: 'left' }
                    ],
                    infoPoint: {
                        id: 'info', box: [0.92, 0.92, 0.00, 0.00], position: 'top',
                        title: t('industryApplications.data.cybersecurity.solutions.identityDetection.infoTitle'),
                        reason: renderInfoReason('industryApplications.data.cybersecurity.solutions.identityDetection.infoReason')
                    }
                },
                { 
                    title: t('industryApplications.data.cybersecurity.solutions.socTriage.title'), 
                    text: t('industryApplications.data.cybersecurity.solutions.socTriage.text'),
                    renderType: 'cyber-pipeline' 
                }
            ]
        },
        {
            id: 'news',
            title: t('industryApplications.data.news.title'),
            subtitle: t('industryApplications.data.news.subtitle'),
            problem: t('industryApplications.data.news.problem'),
            impact: t('industryApplications.data.news.impact'),
            solutionPoints: [
                { 
                    title: t('industryApplications.data.news.solutions.semanticDetection.title'), 
                    text: t('industryApplications.data.news.solutions.semanticDetection.text'),
                    renderType: 'text-highlight', 
                    verifyUrl: 'https://www.vetra-ai.com/verify?id=8efc5f10cf93704021d250d88711eecd3c80417cee170e22983c162fbf895860&ref=c8003531-004f-43aa-8b9b-5b7e4ae5c5e9&context=fake_news_detection&regions=true&explanation=true',
                    shieldIcon: require('./assets/application/vetraai_logo_white.webp'),
                    textContent: t('industryApplications.data.news.solutions.semanticDetection.textContent'),
                    regions: [
                        { quote: newsRegions[0]?.quote, reason: newsRegions[0]?.reason, severity: 8 },
                        { quote: newsRegions[1]?.quote, reason: newsRegions[1]?.reason, severity: 7 },
                        { quote: newsRegions[2]?.quote, reason: newsRegions[2]?.reason, severity: 9 },
                        { quote: newsRegions[3]?.quote, reason: newsRegions[3]?.reason, severity: 10 }
                    ],
                    infoPoint: {
                        id: 'info', position: 'right',
                        title: t('industryApplications.data.news.solutions.semanticDetection.infoTitle'),
                        reason: renderInfoReason('industryApplications.data.news.solutions.semanticDetection.infoReason')
                    }
                },
                { 
                    title: t('industryApplications.data.news.solutions.ingestScanning.title'), 
                    text: t('industryApplications.data.news.solutions.ingestScanning.text'),
                    renderType: 'news-pipeline' 
                }
            ]
        },
        {
            id: 'legal',
            title: t('industryApplications.data.legal.title'),
            subtitle: t('industryApplications.data.legal.subtitle'),
            problem: t('industryApplications.data.legal.problem'),
            impact: t('industryApplications.data.legal.impact'),
            solutionPoints: [
                { 
                    title: t('industryApplications.data.legal.solutions.audioForensics.title'), 
                    text: t('industryApplications.data.legal.solutions.audioForensics.text'),
                    renderType: 'audio-player',
                    mediaImage: require('./assets/application/legal_0.webp'),
                    mediaUrl: 'https://vetra-ai-public-content.s3.eu-central-1.amazonaws.com/audio_industry_applications.mp3', 
                    verifyUrl: 'https://www.vetra-ai.com/verify?id=83148461e7d2632f0db01863888edd37d915089701770787797b22c51b460c6d&ref=c8003531-004f-43aa-8b9b-5b7e4ae5c5e9&ctx=e83d7121294d9185aa53a2b8a84878f9e1f8482b8dd1d70c661d9068556c825f&context=ai_detection&regions=true&explanation=true',
                    shieldIcon: require('./assets/application/vetraai_logo_white.webp'),
                    infoPoint: {
                        id: 'info', position: 'right',
                        title: t('industryApplications.data.legal.solutions.audioForensics.infoTitle'),
                        reason: renderInfoReason('industryApplications.data.legal.solutions.audioForensics.infoReason')
                    }
                },
                { 
                    title: t('industryApplications.data.legal.solutions.auditTrail.title'), 
                    text: t('industryApplications.data.legal.solutions.auditTrail.text'),
                    renderType: 'chain-of-custody'
                }
            ]
        },
        {
            id: 'luxury',
            title: t('industryApplications.data.luxury.title'),
            subtitle: t('industryApplications.data.luxury.subtitle'),
            problem: t('industryApplications.data.luxury.problem'),
            impact: t('industryApplications.data.luxury.impact'),
            solutionPoints: [
                { 
                    title: t('industryApplications.data.luxury.solutions.verifiableListing.title'), 
                    text: t('industryApplications.data.luxury.solutions.verifiableListing.text'),
                    renderType: 'qr-code',
                    verifyUrl: 'https://www.vetra-ai.com/verify?id=0f70100dfa11e05ace05485b25ce499d438fdc5022cb39ab885be04b99d8b3b5&ref=c8003531-004f-43aa-8b9b-5b7e4ae5c5e9&context=ai_detection&regions=true&explanation=true',
                    qrIcon: require('./assets/application/luxury_1_qr.webp'), 
                    qrPosition: 'top-left'
                },
                { 
                    title: t('industryApplications.data.luxury.solutions.apiScreening.title'), 
                    text: t('industryApplications.data.luxury.solutions.apiScreening.text'),
                    renderType: 'luxury-pipeline' 
                }
            ]
        },
        {
            id: 'academia',
            title: t('industryApplications.data.academia.title'),
            subtitle: t('industryApplications.data.academia.subtitle'),
            problem: t('industryApplications.data.academia.problem'),
            impact: t('industryApplications.data.academia.impact'),
            solutionPoints: [
                { 
                    title: t('industryApplications.data.academia.solutions.textVerification.title'), 
                    text: t('industryApplications.data.academia.solutions.textVerification.text'),
                    renderType: 'text-highlight',
                    verifyUrl: 'https://www.vetra-ai.com/verify?id=45f715de90ac315425646cbd116142b1ec85fe36a5fb5355f6466aef5183b2e8&ref=c8003531-004f-43aa-8b9b-5b7e4ae5c5e9&context=ai_detection&regions=true&explanation=true',
                    shieldIcon: require('./assets/application/vetraai_logo_white.webp'),
                    textContent: t('industryApplications.data.academia.solutions.textVerification.textContent'),
                    regions: [
                        { quote: academiaRegions[0]?.quote, reason: academiaRegions[0]?.reason, severity: 7 },
                        { quote: academiaRegions[1]?.quote, reason: academiaRegions[1]?.reason, severity: 6 },
                        { quote: academiaRegions[2]?.quote, reason: academiaRegions[2]?.reason, severity: 8 },
                        { quote: academiaRegions[3]?.quote, reason: academiaRegions[3]?.reason, severity: 7 }
                    ],
                    infoPoint: {
                        id: 'info', position: 'right',
                        title: t('industryApplications.data.academia.solutions.textVerification.infoTitle'),
                        reason: renderInfoReason('industryApplications.data.academia.solutions.textVerification.infoReason')
                    }
                },
                { 
                    title: t('industryApplications.data.academia.solutions.lmsIntegration.title'), 
                    text: t('industryApplications.data.academia.solutions.lmsIntegration.text'),
                    renderType: 'academia-pipeline' 
                }
            ]
        },
        {
            id: 'social',
            title: t('industryApplications.data.social.title'),
            subtitle: t('industryApplications.data.social.subtitle'),
            problem: t('industryApplications.data.social.problem'),
            impact: t('industryApplications.data.social.impact'),
            solutionPoints: [
                { 
                    title: t('industryApplications.data.social.solutions.personaDetection.title'), 
                    text: t('industryApplications.data.social.solutions.personaDetection.text'),
                    renderType: 'heatmap',
                    regions: [
                        { id: 'hands', box: [0.37, 0.66, 0.66, 0.87], reason: t('industryApplications.data.social.solutions.personaDetection.regions.hands'), severity: 8, position: 'top' },
                        { id: 'skin', box: [0.35, 0.15, 0.55, 0.45], reason: t('industryApplications.data.social.solutions.personaDetection.regions.skin'), severity: 7, position: 'right' },
                        { id: 'sweater', box: [0.15, 0.5, 0.4, 0.8], reason: t('industryApplications.data.social.solutions.personaDetection.regions.sweater'), severity: 6, position: 'right' },
                        { id: 'plant', box: [0.68, 0.25, 0.98, 0.63], reason: t('industryApplications.data.social.solutions.personaDetection.regions.plant'), severity: 5, position: 'left' }
                    ],
                    infoPoint: {
                        id: 'info', box: [0.92, 0.92, 0.00, 0.00], position: 'top',
                        title: t('industryApplications.data.social.solutions.personaDetection.infoTitle'),
                        reason: renderInfoReason('industryApplications.data.social.solutions.personaDetection.infoReason')
                    }
                },
                { 
                    title: t('industryApplications.data.social.solutions.badging.title'), 
                    text: t('industryApplications.data.social.solutions.badging.text'),
                    renderType: 'shield',
                    verifyUrl: 'https://www.vetra-ai.com/verify?id=5ac44bea4781c9b32716bba01856ab4409cf3e7951d0b2b397f6dd8d95605c89&ref=c8003531-004f-43aa-8b9b-5b7e4ae5c5e9&context=ai_detection&regions=true&explanation=true',
                    shieldIcon: require('./assets/application/vetraai_logo_social_1.webp'),
                    shieldPosition: 'top-left',
                    tooltipColor: '#150e0a',
                    floatingIcons: [
                        { emoji: '👍', top: '50%', left: '70%', delay: 0 },
                        { emoji: '❤️', top: '65%', left: '60%', delay: 0.8 },
                        { emoji: '🔥', top: '45%', left: '80%', delay: 1.6 }
                    ]
                }
            ]
        }
    ];
};